import { useState } from "react";
import { testimonials } from "../../../mocks/episodes";

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const t = testimonials[active];

  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !comment.trim() || selected === 0) return;
    setSubmitStatus("submitting");

    const body = new URLSearchParams({
      name,
      rating: String(selected),
      comment,
    });

    try {
      await fetch("https://readdy.ai/api/form/d73lqjucsbv12mkucpag", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitStatus("success");
      setName("");
      setComment("");
      setSelected(0);
      setHovered(0);
    } catch {
      setSubmitStatus("error");
    }
  };

  return (
    <section className="bg-cream py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <span className="font-sans text-xs font-semibold tracking-widest uppercase text-amber-600 block mb-4">
            Listener Voices
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-amber-900">
            Real People, Real Impact
          </h2>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Quote */}
          <div className="relative bg-white rounded-3xl p-10 lg:p-14 text-center hover:-translate-y-1 transition-all duration-300">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 rounded-full bg-amber-100">
              <i className="ri-double-quotes-l text-amber-600 text-2xl" />
            </div>
            <blockquote className="font-serif text-xl lg:text-2xl text-amber-900 leading-relaxed mb-8 italic">
              &ldquo;{t.quote}&rdquo;
            </blockquote>
            <div className="flex flex-col items-center gap-1">
              <p className="font-sans font-semibold text-amber-800 text-base">{t.name}</p>
              <p className="font-sans text-sm text-stone-400">{t.role}</p>
              <div className="flex items-center gap-1.5 mt-2">
                <i className="ri-star-fill text-amber-500 text-xs" />
                <i className="ri-star-fill text-amber-500 text-xs" />
                <i className="ri-star-fill text-amber-500 text-xs" />
                <i className="ri-star-fill text-amber-500 text-xs" />
                <i className="ri-star-fill text-amber-500 text-xs" />
                <span className="font-sans text-xs text-stone-400 ml-1">on {t.platform}</span>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-3 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`rounded-full transition-all duration-200 cursor-pointer ${
                  i === active
                    ? "w-8 h-3 bg-amber-600"
                    : "w-3 h-3 bg-amber-200 hover:bg-amber-300"
                }`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 text-center">
          {[
            { value: "0", label: "Episodes" },
            { value: "0", label: "Monthly Listeners" },
            { value: "0★", label: "Average Rating" },
            { value: "3", label: "Platforms" },
          ].map((s) => (
            <div key={s.label}>
              <p className="font-serif text-4xl font-bold text-amber-700 mb-1">{s.value}</p>
              <p className="font-sans text-sm text-stone-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Share Your Voice Form */}
        <div className="max-w-2xl mx-auto mt-20">
          <div className="text-center mb-10">
            <span className="font-sans text-xs font-semibold tracking-widest uppercase text-amber-600 block mb-3">
              Share Your Voice
            </span>
            <h3 className="font-serif text-3xl font-bold text-amber-900 mb-3">
              Has This Podcast Moved You?
            </h3>
            <p className="font-sans text-sm text-stone-500 leading-relaxed">
              We&apos;d love to hear your story. Leave a comment and we may feature it right here.
            </p>
          </div>

          {submitStatus === "success" ? (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-10 text-center">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-amber-100">
                <i className="ri-heart-fill text-amber-600 text-2xl" />
              </div>
              <h4 className="font-serif text-xl font-bold text-amber-900 mb-2">Thank You!</h4>
              <p className="font-sans text-sm text-stone-500">
                Your voice has been received. We&apos;ll be in touch if we feature your comment.
              </p>
              <button
                onClick={() => setSubmitStatus("idle")}
                className="mt-6 font-sans text-sm text-amber-600 underline underline-offset-2 cursor-pointer hover:text-amber-800 transition-colors whitespace-nowrap"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 lg:p-10 space-y-6"
            >
              {/* Name */}
              <div>
                <label className="font-sans text-xs font-semibold tracking-widest uppercase text-stone-400 block mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Jordan M."
                  required
                  className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm font-sans text-amber-900 placeholder-stone-300 focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              {/* Star Rating */}
              <div>
                <label className="font-sans text-xs font-semibold tracking-widest uppercase text-stone-400 block mb-3">
                  Your Rating
                </label>
                <input type="hidden" name="rating" value={selected} />
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setSelected(star)}
                      onMouseEnter={() => setHovered(star)}
                      onMouseLeave={() => setHovered(0)}
                      className="w-9 h-9 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                      aria-label={`${star} star`}
                    >
                      <i
                        className={`text-2xl transition-colors ${
                          star <= (hovered || selected)
                            ? "ri-star-fill text-amber-500"
                            : "ri-star-line text-stone-300"
                        }`}
                      />
                    </button>
                  ))}
                  {selected > 0 && (
                    <span className="font-sans text-xs text-stone-400 ml-1">
                      {["", "Poor", "Fair", "Good", "Great", "Amazing!"][selected]}
                    </span>
                  )}
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="font-sans text-xs font-semibold tracking-widest uppercase text-stone-400 block mb-2">
                  Your Comment
                </label>
                <textarea
                  name="comment"
                  value={comment}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setComment(e.target.value);
                  }}
                  placeholder="Tell us how the podcast has impacted you..."
                  required
                  rows={4}
                  className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm font-sans text-amber-900 placeholder-stone-300 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                />
                <p className={`font-sans text-xs mt-1 text-right ${comment.length >= 500 ? "text-red-400" : "text-stone-300"}`}>
                  {comment.length}/500
                </p>
              </div>

              {submitStatus === "error" && (
                <p className="font-sans text-xs text-red-400 text-center">
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={submitStatus === "submitting" || !name.trim() || !comment.trim() || selected === 0}
                className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-amber-200 disabled:cursor-not-allowed text-white font-sans font-semibold text-sm py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {submitStatus === "submitting" ? "Sending..." : "Send My Voice"}
              </button>
            </form>
          )}
        </div>

      </div>
    </section>
  );
}
